/// <amd-module name="DistributedTask/Common/AadAuthorizer/DelayedFunction" />
interface IArgsFunctionR<TResult> {
    (...args: any[]): TResult;
}

export function delegate(instance: any, method: Function, data?: any): IArgsFunctionR<any> {

    return function () {
        if (typeof (data) === "undefined") {
            return method.apply(instance, arguments);
        }
        else {
            let args = <any[]>Array.prototype.slice.call(arguments, 0);

            if (data instanceof Array) {
                args = args.concat(data);
            }
            else {
                args.push(data);
            }

            return method.apply(instance, args);
        }
    };
}

export class DelayedFunction {

    private _interval: number;
    private _func: IArgsFunctionR<any>;
    private _timeoutHandle?: number;
    private _cooldownHandle?: number;
    private _name: string;
    private _invokeOnCooldownComplete: boolean = false;

    /**
     * Creates an object that can be used to delay-execute the specified method.
     * 
     * @param instance Context to use when calling the provided function
     * @param ms Delay in milliseconds to wait before executing the Function
     * @param name Name to use when tracing this delayed function
     * @param method Method to execute
     * @param data Arguments to pass to the method
     */
    constructor(instance: any, ms: number, name: string, method: Function, data?: any[]) {
        this._interval = ms;
        this._name = name;
        this._func = delegate(instance, method, data);
    }

    /**
     * Starts the timer (if not already started) which will invoke the method once expired.
     */
    public start() {
        let that = this;
        if (!this._timeoutHandle) {
            //Diag.logTracePoint("Core.DelayedFunction.pending", this._name);
            this._timeoutHandle = window.setTimeout(function () {
                delete that._timeoutHandle;
                try {
                    that._invoke.call(that);
                }
                finally {
                    //Diag.logTracePoint("Core.DelayedFunction.complete", [that._name, "complete"]);
                }
            }, this._interval);
        }
    }

    /**
     * Resets the timer (cancel, then re-start) which will invoke the method once expired.
     */
    public reset() {
        this.cancel();
        //Diag.logTracePoint("Core.DelayedFunction.reset", [this._name, "reset"]);
        this.start();
    }

    /**
     * Cancels any pending operation (stops the timer).
     */
    public cancel(clearCooldown: boolean = false) {
        if (this._timeoutHandle) {
            window.clearTimeout(this._timeoutHandle);
            delete this._timeoutHandle;
            //Diag.logTracePoint("Core.DelayedFunction.complete", [this._name, "canceled"]);
        }
        if (clearCooldown) {
            this.clearCooldown();
        }

        // After cancel is called, we don't expect the function to run again.
        this._invokeOnCooldownComplete = false;
    }

    /**
     * Clears the current cooldown
     * @param cancelScheduledInvocation (boolean) true to ignore any invocation that is
     *        scheduled to occur after the cooldown is finished.
     */
    public clearCooldown(cancelScheduledInvocation: boolean = true) {
        if (this._cooldownHandle) {
            window.clearTimeout(this._cooldownHandle);
            delete this._cooldownHandle;
        }
        if (!cancelScheduledInvocation && this.invokeOnCooldownComplete) {
            this.invokeNow();
        }
    }

    /**
     * Resets the cooldown back to [delay] ms.
     */
    public extendCooldown() {
        this._startCooldown();
    }

    /**
     * Invokes the method immediately (canceling an existing timer).
     */
    public invokeNow() {
        this.cancel();
        this._invoke();
    }

    /**
     * Modifies the length of the delay timer (for subsequent starts).
     * 
     * @param ms Delay in milliseconds to wait before executing the Function
     */
    public setDelay(ms: number) {
        this._interval = ms;
    }

    /**
     * Modify the method being executed.
     * 
     * @param instance Context to use when calling the provided function
     * @param method Method to execute
     * @param data (Optional) arguments to pass to the method
     */
    public setMethod(instance: any, method: Function, data?: any[]) {
        this._func = delegate(instance, method, data);
    }

    /**
     * Is the timer currently running (operation in progress)
     * 
     * @return True if this operation is already in progress
     */
    public isPending(): boolean {
        return this._timeoutHandle ? true : false;
    }

    /**
     * Is the delayed function in a "cooldown" state (operation
     * completed recently)
     *
     * @return True if it has been less than [delay] ms since
     * the last invocation of the function.
     */
    public isCoolingDown(): boolean {
        return this._cooldownHandle ? true : false;
    }

    /**
     * Schedule this delayed function to execute when the cooldown is complete.
     */
    public invokeOnCooldownComplete() {
        this._invokeOnCooldownComplete = true;
    }

    /**
     * Invokes the delegate and starts (or restars) the cooldown period.
     */
    private _invoke() {
        this._func();
        this._startCooldown();
    }

    private _startCooldown() {
        if (this._cooldownHandle) {
            window.clearTimeout(this._cooldownHandle);
        }
        this._cooldownHandle = window.setTimeout(() => {
            delete this._cooldownHandle;
            if (this._invokeOnCooldownComplete) {
                this.invokeNow();
                this._invokeOnCooldownComplete = false;
            }
        }, this._interval);
    }
}
